import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoggingService } from './logging.service';

/**
 * Parsed section descriptor derived from a router URL. `section` is the stable
 * usage-analytics key (top-level area or customer sub-tab); the id fields are
 * attached to the emitted `section.open` event but never affect dedupe.
 *
 * @ticket EB-3798
 */
interface SectionInfo {
  section: string;
  customerId?: number;
  phoneServiceId?: number;
  userAccountId?: number;
}

/**
 * Emits AMIE's client-initiated usage events (`app.open`, `section.open`) via
 * {@link LoggingService}.
 *
 * @ticket EB-3798
 * Lifecycle mirrors {@link VersionService}: a single idempotent `start()` wired
 * into app bootstrap AFTER `AuthService.configure()` resolves, plus a `stop()`
 * for test teardown. These are AMIE-initiated events, so they carry NO
 * correlationId (D5).
 *
 * `section.open` dedupe (D10 / Feature-4 edge-case): the derived KEY is the
 * COMPOSITE of section + customerId + phoneServiceId + userAccountId. The
 * telemetry goal is "which sections are used, per customer", so a customer/PS/UA
 * switch on the same tab (e.g. customer A contracts → customer B contracts) is a
 * real section-open and MUST re-emit. Only true noise is suppressed: emitting
 * only when that composite key changes drops redirect chains landing on the SAME
 * resolved resource and query-param-only re-nav. The initial landing key is
 * seeded from the current URL at `start()` so the first `NavigationEnd` (the
 * `''`→landing redirect) does not double the `app.open` that already marks the
 * session start.
 */
@Injectable({ providedIn: 'root' })
export class UsageTrackingService {
  private readonly loggingService = inject(LoggingService);
  private readonly router = inject(Router);

  private started = false;
  private navigationSub: Subscription | null = null;

  /** Last emitted composite key; seeded at `start()` so the landing isn't re-emitted. */
  private lastSectionKey: string | null = null;

  /**
   * Begin usage tracking. Idempotent — a second call is a no-op.
   *
   * Emits `app.open` exactly once, seeds the current section as already-open
   * (so the initial navigation doesn't double `app.open`), then emits
   * `section.open` on every `NavigationEnd` that changes the section key.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    this.loggingService.usage('app.open', { appVersion: environment.appVersion });

    const seed = this.deriveSection(this.router.url);
    this.lastSectionKey = seed ? this.compositeKey(seed) : null;

    this.navigationSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.onNavigationEnd(event));
  }

  /** Release the navigation subscription. Idempotent — safe to call in teardown. */
  stop(): void {
    this.navigationSub?.unsubscribe();
    this.navigationSub = null;
    this.started = false;
    this.lastSectionKey = null;
  }

  private onNavigationEnd(event: NavigationEnd): void {
    const info = this.deriveSection(event.urlAfterRedirects);
    if (!info) return;

    const key = this.compositeKey(info);
    if (key === this.lastSectionKey) return;

    this.lastSectionKey = key;

    const fields: Record<string, unknown> = { section: info.section };
    if (info.customerId !== undefined) fields['customerId'] = info.customerId;
    if (info.phoneServiceId !== undefined) fields['phoneServiceId'] = info.phoneServiceId;
    if (info.userAccountId !== undefined) fields['userAccountId'] = info.userAccountId;

    this.loggingService.usage('section.open', fields);
  }

  /**
   * Dedupe key = section + all path ids, so a customer/PS/UA switch on the same
   * tab counts as a new section-open while redirect chains / query-param-only
   * re-nav landing on the SAME resolved resource stay suppressed.
   */
  private compositeKey(info: SectionInfo): string {
    return `${info.section}|${info.customerId ?? ''}|${info.phoneServiceId ?? ''}|${info.userAccountId ?? ''}`;
  }

  /**
   * Map a router URL to a stable section + the ids carried in its path. Mirrors
   * the `customer-detail.component.ts` `updateActiveTab` segment parsing.
   * Returns null for URLs that map to no known section (defensive).
   */
  private deriveSection(url: string): SectionInfo | null {
    const path = url.split('#')[0].split('?')[0];
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) return { section: 'search' };

    const first = segments[0];
    if (first === 'calls') return { section: 'calls' };
    if (first === 'classic-search') return { section: 'classic-search' };
    if (first === 'admin') return { section: 'admin' };

    const customerId = Number(first);
    // A non-numeric, non-keyword first segment is an unrecognised top-level
    // route: emit an explicit `unknown` section (never silently drop the event,
    // or the NEXT route added here loses `section.open` invisibly).
    if (!Number.isFinite(customerId) || customerId <= 0) return { section: 'unknown' };

    // `/:customerId` alone redirects to `information` — treat it as such.
    if (segments.length === 1) return { section: 'information', customerId };

    const sub = segments[1];
    if (sub === 'ua') {
      const leaf = segments[3];
      const section = leaf === 'communication' ? 'communication' : 'ua';
      return { section, customerId, userAccountId: this.positiveId(segments[2]) };
    }
    if (sub === 'ps') {
      const phoneServiceId = this.positiveId(segments[2]);
      const leaf = segments[3];
      // A leaf-less `/:id/ps/:id` resolves to the PS settings section; guard the
      // `undefined` leaf so `section.open` never emits `section: undefined`.
      // Namespace the leaf (`ps.calls`) so a per-PS tab does not collide with a
      // top-level section of the same name (e.g. the global `/calls` viewer).
      const section = !leaf || leaf === 'settings' ? 'ps' : `ps.${leaf}`;
      return { section, customerId, phoneServiceId };
    }

    return { section: sub, customerId };
  }

  /**
   * Parse a positive numeric path id, or `undefined` when the segment is missing
   * or non-numeric — so a malformed `/:id/ps` never emits `phoneServiceId: NaN`
   * (which lands as the literal string `"NaN"` in Graylog).
   */
  private positiveId(segment: string | undefined): number | undefined {
    const id = Number(segment);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }
}
